import { datadogRum } from '@datadog/browser-rum';
import { STORAGE_KEYS } from '@constants/index';

export interface UtmParams {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
}

const MAX_UTM_LENGTH = 200;
const UTM_TOKEN = /^[a-zA-Z0-9._%-]{1,200}$/;

function sanitizeUtmValue(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const clean = raw
    .trim()
    .slice(0, MAX_UTM_LENGTH)
    .replace(/[^a-zA-Z0-9._%-]/g, '');
  return clean && UTM_TOKEN.test(clean) ? clean : undefined;
}

export function sanitizeUtm(utm: UtmParams): UtmParams {
  return {
    source: sanitizeUtmValue(utm.source),
    medium: sanitizeUtmValue(utm.medium),
    campaign: sanitizeUtmValue(utm.campaign),
    content: sanitizeUtmValue(utm.content),
  };
}

function parseUtmFromUrl(): UtmParams | null {
  const params = new URLSearchParams(window.location.search);
  const raw: UtmParams = {
    source: params.get('utm_source') ?? undefined,
    medium: params.get('utm_medium') ?? undefined,
    campaign: params.get('utm_campaign') ?? undefined,
    content: params.get('utm_content') ?? undefined,
  };

  if (!raw.source && !raw.medium && !raw.campaign && !raw.content) return null;

  return sanitizeUtm(raw);
}

function encodeUtmForStorage(utm: UtmParams): string {
  const safe = sanitizeUtm(utm);
  return encodeURIComponent(JSON.stringify(safe));
}

function decodeUtmFromStorage(raw: string | null): UtmParams | null {
  if (!raw) return null;
  try {
    const json = raw.includes('%') ? decodeURIComponent(raw) : raw;
    const parsed = JSON.parse(json) as UtmParams;
    return sanitizeUtm(parsed);
  } catch {
    return null;
  }
}

/** Call once at app boot. Persists allowlisted UTMs; fires Datadog re-engagement. */
export function captureUtmParams(): void {
  const utm = parseUtmFromUrl();
  if (!utm) return;

  const safe = sanitizeUtm(utm);
  const payload = encodeUtmForStorage(safe);

  try {
    if (!localStorage.getItem(STORAGE_KEYS.FIRST_UTM)) {
      localStorage.setItem(STORAGE_KEYS.FIRST_UTM, payload); // NOSONAR
      console.debug('[UTM] Captured first-visit UTMs:', safe);
    }
  } catch (e) {
    console.debug('[UTM] localStorage unavailable, skipping first-visit write', e);
  }

  try {
    sessionStorage.setItem(STORAGE_KEYS.SESSION_UTM, payload); // NOSONAR
    console.debug('[UTM] Session UTMs:', safe);
  } catch (e) {
    console.debug('[UTM] sessionStorage unavailable, skipping session write', e);
  }

  try {
    if (datadogRum.getInitConfiguration()) {
      datadogRum.addAction('utm_reengagement', safe);
    }
  } catch (e) {
    console.debug('[UTM] Failed to send RUM action', e);
  }
}

export function getFirstVisitUtm(): UtmParams | null {
  try {
    return decodeUtmFromStorage(localStorage.getItem(STORAGE_KEYS.FIRST_UTM));
  } catch {
    return null;
  }
}

export function getSessionUtm(): UtmParams | null {
  try {
    return decodeUtmFromStorage(sessionStorage.getItem(STORAGE_KEYS.SESSION_UTM));
  } catch {
    return null;
  }
}
